export type AppErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'NETWORK'
  | 'NOT_CONFIGURED'
  | 'TIMEOUT'
  | 'INCOMPLETE'
  | 'HTML_RESPONSE'
  | 'INVALID_RESPONSE_TYPE'
  | 'ENDPOINT_UNAVAILABLE'
  | 'PROVIDER_ERROR'
  | 'ABORTED'
  | 'LOCAL_AI_UNSUPPORTED'
  | 'LOCAL_AI_NOT_READY'
  | 'LOCAL_AI_DOWNLOAD_FAILED'
  | 'LOCAL_AI_INFERENCE_FAILED'
  | 'LOCAL_AI_EMBEDDINGS_UNAVAILABLE'
  | 'LOCAL_AI_OOM'
  | 'LOCAL_AI_TIMEOUT'
  | 'PASSKEY_NOT_SUPPORTED'
  | 'PASSKEY_CANCELLED'
  | 'UNKNOWN'

export interface AppErrorOptions {
  code?: AppErrorCode
  status?: number
  cause?: unknown
}

export class AppError extends Error {
  code: AppErrorCode
  status: number
  cause?: unknown

  constructor(message: string, options: AppErrorOptions = {}) {
    super(message)
    this.name = 'AppError'
    this.code = options.code ?? 'UNKNOWN'
    this.status = options.status ?? 500
    this.cause = options.cause
  }
}

const inferCodeByStatus = (status: number): AppErrorCode => {
  if (status === 401) return 'UNAUTHORIZED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 422 || status === 400) return 'VALIDATION'
  if (status === 0) return 'NETWORK'
  if (status === 408 || status === 504) return 'TIMEOUT'
  if (status === 502) return 'INVALID_RESPONSE_TYPE'
  if (status === 503) return 'ENDPOINT_UNAVAILABLE'
  return 'UNKNOWN'
}

const hasMessage = (value: unknown): value is { message: string } =>
  Boolean(value) &&
  typeof value === 'object' &&
  typeof (value as { message?: unknown }).message === 'string'

const hasStatus = (value: unknown): value is { status: number | string } =>
  Boolean(value) &&
  typeof value === 'object' &&
  (typeof (value as { status?: unknown }).status === 'number' ||
    typeof (value as { status?: unknown }).status === 'string')

export const toAppError = (
  error: unknown,
  fallbackMessage = 'Erro inesperado.',
  fallbackStatus = 500,
): AppError => {
  if (error instanceof AppError) {
    return error
  }

  const parsedStatus = hasStatus(error)
    ? typeof error.status === 'number'
      ? error.status
      : Number.parseInt(error.status, 10)
    : Number.NaN

  const status = Number.isFinite(parsedStatus) ? parsedStatus : fallbackStatus
  const message = hasMessage(error) ? error.message : fallbackMessage

  return new AppError(message, {
    status,
    code: inferCodeByStatus(status),
    cause: error,
  })
}
